import { ActualItemOrigin, Enum, ItemClass, PricingType, ProjectStatus, ProjectType, VisitWorkareaStatus } from "../../common/constants"
import api from "../../components/api"
import { SaveVisitPayload } from "../visits/editVisitApi"

export type CrewMember = {
  id: number
  name: string
  avatar: string
  isActive: boolean
  userEmail: string
  hasCustomAvatar?: boolean
  hasStockAvatar?: boolean
  color?: string
}

export const loadCrew = async (): Promise<CrewMember[]> => {
  return (await api.get(`/users/crew`)).data
}

export type CalendarEventVisit = {
  id: number
  calendarEventId: number
  visitId: string
}

export type ScheduledEventsDayView = {
  scheduledEvents: ScheduledEvent[]
  calendarEventVisits: CalendarEventVisit[]
  visits: Visit[]
  visitWorkareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
}

export type Timesheet = {
  id: string
  timecardId: string
  crewMemberId: number
  projectId: number
  projectWorkareaId?: number
  startDateUtc: string
  endDateUtc: string
  total: number
  lunch: number
  status: number
  totalTime: number
  canBeEdited: boolean
}

export type Visit = {
  id: string
  status: number
  internalNotes: string | null
  projectId: number | null
  dateUtc: string
}

export type UpsertVisitRequest = {
  visitId: string
  internalNotes: string | null
  jobId: number | null
}

export type UpdateVisitWorkareaStatusRequest = {
  visitId: string
  jobId: number
  projectStatus: ProjectStatus | null
  workareas: UpdateWorkareaStatusRequest[]
}

export type UpdateWorkareaStatusRequest = {
  workareaId: number
  status: VisitWorkareaStatus
}

export type VisitWorkareaStatusRecord = {
  visitId: string
  workareaId: number
  status: VisitWorkareaStatus
  workareaName: string | null
}

export type FilterableEvent = {
  id: number
  crewForemanEmail: string | null
  crewMemberIds: string | null
  projectType: number | null
  projectAllDivisionIds: string
  salesPersonEmail: string | null
  createdByVisit: boolean
}

export type ScheduledEvent = FilterableEvent & {
  jobName: string
  jobNumber?: string | null
  jobId: number
  startUtc: string
  start: string
  endUtc: string
  end: string
  crewForemanColor: string
  customerId: number | null
  customerDisplayName: string | null
  customerUrl: string | null
  customerAddress1: string | null
  customerAddress2: string | null
  customerCity: string | null
  customerState: string | null
  customerZip: string | null
  customerCounty: string | null
  customerCountryCode: string | null
  propertyNickname: string | null
  projectSold: boolean | null
  workAreaIds: string | null
  workAreaNames: string | null
  routeId: number | null
  routeName: string | null
  propertyId: number | null
  latitude: number | null
  longitude: number | null
  repeatingId: string | null
  allDay: boolean
  frozen: boolean
  schedulingNotes: string | null
  notes: string | null
  sendCrewLeadNotifications: boolean
  createdByVisit: boolean
}

export type DayViewEvent = FilterableEvent & {
  jobName: string
  jobNumber?: string | null
  jobId: number
  startUtc: string
  start: string
  endUtc: string
  end: string
  allDay: boolean
  frozen: boolean
  repeatingId: string | null
  crewForemanColor: string
  customerDisplayName: string | null
  customerAddress1: string | null
  customerAddress2: string | null
  customerCity: string | null
  customerState: string | null
  customerZip: string | null
  customerCounty: string | null
  customerCountryCode: string | null
  propertyNickname: string | null
  propertyId: number | null
  latitude: number | null
  longitude: number | null
  routeId: number | null
  routeName: string | null
  workAreaIds: string | null
  workAreaNames: string | null
  schedulingNotes: string | null
  notes: string | null
  sendCrewLeadNotifications: boolean
  createdByVisit: boolean
}

type DayViewResponse = {
  events: DayViewEvent[]
  calendarEventVisits: CalendarEventVisit[]
  visits: Visit[]
  visitWorkareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
}

export type DateRangeViewEventWorkArea = {
  id: number
  name: string
  divisionId?: number
  isApproved: boolean
  isOnHold: boolean
  estimatedHours: number
  isRecurring: boolean
  pricingType: PricingType
  status?: ProjectStatus
}

export type DateRangeViewEvent = FilterableEvent & {
  jobName: string
  jobNumber?: string | null
  jobId: number
  startUtc: string
  start: string
  endUtc: string
  end: string
  allDay: boolean
  frozen: boolean
  repeatingId: string | null
  crewForemanColor: string
  routeId: number | null
  workAreaIds: string | null
  workAreas: DateRangeViewEventWorkArea[]
  customer: {
    displayName: string | null
  } | undefined
  property: {
    nickname: string | null
    address1: string | null
    address2: string | null
    city: string | null
    state: string | null
    zip: string | null
    county: string | null
    countryCode: string | null
    id: number | null
    latitude: number | null
    longitude: number | null
  } | undefined
  schedulingNotes: string | null
  notes: string | null
  sendCrewLeadNotifications: boolean
  createdByVisit: boolean
  allApprovedWorkAreasComplete: boolean
  rescheduledToEventId: number | null
}

export type DateRangeViewResponse = {
  events: DateRangeViewEvent[]
  calendarEventVisits: CalendarEventVisit[]
  visits: Visit[]
  visitWorkareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
  visitEvents: VisitEvent[]
}

export const loadScheduledEventsByDate = async (date: string): Promise<ScheduledEventsDayView> => {
  return (await api.get(`/web/scheduling/events/${date}`)).data
}

export const loadDayViewEvents = async (date: string): Promise<DayViewResponse> => {
  return (await api.get(`/web/scheduling/events/dayview?date=${date}`)).data
}

export const loadDateRangeViewEvents = async (startDate: string, endDate: string): Promise<DateRangeViewResponse> => {
  return (await api.get(`/web/scheduling/events/daterange?startDate=${startDate}&endDate=${endDate}`)).data
}

export type VisitEvent = Omit<DateRangeViewEvent, 'id'> & {
  visitId: string
}

export const updateScheduledEventCrewLead = async (eventId: number, crewForemanEmail: string | null, crewMemberIds: string | null): Promise<void> => {
  return (await api.post(`/web/scheduling/event/${eventId}/crew-foreman`, { crewMemberIds, crewForemanEmail })).data
}

export const updateVisitNotes = async (request: UpsertVisitRequest): Promise<void> => {
  return (await api.post(`/web/visits`, { ...request })).data
}

export const updateVisitWorkareaStatus = async (request: UpdateVisitWorkareaStatusRequest): Promise<void> => {
  return (await api.post(`/web/visits/status`, { ...request })).data
}

export type CatalogItem = {
  id: number
  guid: string
  tenantId: number
  class: ItemClass
  name: string
  description?: string
  sku?: string
  units?: string
  cost: number
  taxable: boolean
  overtimeFactor?: number
  unbillablePercent?: number
  laborBurden?: number
  customPrice?: number
  customProfitMargin?: number
  categories: string[]
  tags: string[]
  rateInstallationQuantity?: number
  ratePerUnits?: string
  rateUsageQuantity?: number
  adjustedCost: number
  isHidden: boolean
}

export type WorkareaItemBase = {
  id: number
  guid: string
  tenantId: number
  workareaId: number
  catalogItemId: number
  catalogItemGuid: string
  name: string
  catalogName: string
  units: string
  class: ItemClass
  quantity: number
  isBillable: boolean
  customCost?: number
  customUnitPrice?: number
  customProfitMargin?: number
  cost: number
  overhead: number
  tax: number
  profit: number
  originalCatalogCustomPrice?: number
  catalogCustomPrice?: number
  catalogCustomProfitMargin?: number
  catalogCost: number
  chargeTaxOnInvoice: boolean
  applyTaxToBreakeven: boolean
  originalProfitMargin: number
  taxPercentage: number
  overheadPercentage: number

  sortIndex: number

  profitMargin: number
  breakeven: number
  unitPrice: number
  totalPrice: number
  totalBreakeven: number
}

export type EstimatedItem = WorkareaItemBase & {
  templateItemId?: number
}

export type ActualItem = WorkareaItemBase & {
  isBillableUnknown: boolean
  isAdditional: boolean
  visitId?: string
  invoiceId?: number
  origin?: ActualItemOrigin
  originOwner?: string
  isFromTimesheets: boolean
  customTotalPrice?: number
  receiptId?: number
}

export type GetJobWorkareaItemsResponse = {
  estimatedItems: EstimatedItem[],
  actualItems: ActualItem[]
  catalogItems: CatalogItem[]
}

export const getJobItems = async (jobId: number): Promise<GetJobWorkareaItemsResponse> => {
  return (await api.get(`/web/workarea-items/job/${jobId}`)).data
}

export type Job = {
  id: number
  name: string
  sold: boolean
  workareas: Workarea[]
  visits: Visit[]
  type: ProjectType
  status?: ProjectStatus
  property: {
    mapPosition: string
  },
  customer: {
    displayName: string
    phones: {
      description: string
      number: string
      isPrimary: boolean
    }[]
  }
}

export type Workarea = {
  id: number
  name: string
  notes: string
  scheduleDate?: string
  pricingType: PricingType
  status?: ProjectStatus
  isApproved: boolean
  isOnHold?: boolean
  estimatedOccurrences?: number
}

export const getJob = async (id: number): Promise<Job> => {
  return (await api.get(`/jobs/${id}`)).data
}

export const removeScheduledEvent = async (eventId: number): Promise<void> => {
  return (await api.delete(`/web/scheduling/event/${eventId}`)).data
}

export type DeleteScheduledEventsRequest = {
  start: string | null
  end: string | null
  jobId: number
}

export const removeScheduledEventsByRange = async (eventId: number, request: DeleteScheduledEventsRequest): Promise<void> => {
  return (await api.post(`/web/scheduling/events/${eventId}/range`, { start: request.start, end: request.end, jobId: request.jobId })).data
}

export const removeScheduledEvents = async (eventId: number): Promise<void> => {
  return (await api.post(`/web/scheduling/events/${eventId}`)).data
}

export const getRepeatingEventConfig = async (repeatingId: string): Promise<RepeatingEventConfig> => {
  return (await api.get(`/web/scheduling/repeating-config/${repeatingId}`)).data
}

export const createVisitForScheduledEvent = async (eventId: number, request: SaveVisitPayload): Promise<void> => {
  return (await api.post(`/web/scheduling/create-visit/${eventId}`, request)).data
}

export type SchedulingJob = {
  jobId: number
  jobName: string
  jobNumber?: string
  customerId: number
  customerDisplayName: string
  active: boolean
  allDivisionIds: string
  type: ProjectType
  status?: ProjectStatus
  address1: string
  address2: string | null
  city: string
  state: string
  zip: string
  calendarEventCount: number
  hasApprovedWorkareas: boolean
  soldDateUtc?: string
  schedulingNotes?: string
  approvedWorkAreas: {
    name: string
    calendarEventCount: number
    status?: ProjectStatus
    divisionId?: number
  }[]
}

export const getSchedulingJobs = async (): Promise<SchedulingJob[]> => {
  return (await api.get(`/web/scheduling/jobs/v2`)).data
}

export const getScheduledJobsByDate = async (date: string, crewLeadEmail: string): Promise<SchedulingJob[]> => {
  return (await api.get(`/web/scheduling/jobs/scheduled`, { params: { date, crewLeadEmail } })).data
}

export type RepeatingEventConfig = {
  startDate: string
  endDate: string | null
  repeatUnits: string
  repeatCount?: number
  repeatingDayUnit?: string
  repeatPlace: string | null
  repeatQuarter: string | null
  repeatMonth: string | null
}

export type CreateScheduledEventByJobRequest = {
  jobId: number
  start: string
  end: string
  dayStartTime: string | null
  estimatedDurationInMinutes: number
  crewMemberIds: string
  crewLeadId: number | null
  workareaIds: string
  routeId: number | null
  repeatingEventConfig?: RepeatingEventConfig | null
  hasApprovedWorkareas: boolean
  allDay: boolean
  sendCrewLeadNotifications: boolean
  notes?: string | null
  frozen: boolean
}

export const createScheduledEventByJob = async (request: CreateScheduledEventByJobRequest): Promise<void> => {
  return (await api.post(`/web/scheduling/events/createByJob`, request)).data
}

export type CreateByJobEventItem = {
  start: string
  end: string
  allDay: boolean
  estimatedDurationInMinutes: number
  crewLeadId: number | null
  crewMemberIds: string | null
  notes: string | null
}

export type CreateByJobCadenceGroup = {
  repeatingId: string | null
  repeatingEventConfig: RepeatingEventConfig | null
  workareaIds: string | null
  sendCrewLeadNotifications: boolean
  events: CreateByJobEventItem[]
}

export type CreateScheduledEventsByJobV2Request = {
  jobId: number
  hasApprovedWorkareas: boolean
  cadences: CreateByJobCadenceGroup[]
}

export const createScheduledEventsByJobV2 = async (request: CreateScheduledEventsByJobV2Request): Promise<void> => {
  return (await api.post(`/web/scheduling/events/createByJob/v2`, request)).data
}

export type UpsertTaskEventRequest = {
  eventId?: number | null
  title: string
  notes: string | null
  start: string
  end: string
  dayStartTime: string | null
  estimatedDurationInMinutes: number
  crewLeadId: number | null
  crewMemberIds: string
  allDay: boolean
  sendCrewLeadNotifications: boolean
  repeatingEventConfig?: RepeatingEventConfig | null
  completedAt?: string | null
  frozen: boolean
}

export const upsertTaskEvent = async (request: UpsertTaskEventRequest): Promise<void> => {
  return (await api.put(`/web/scheduling/tasks`, request)).data
}

export const TaskCompletionScopes = {
  ThisEvent: 'ThisEvent',
  ThisAndFuture: 'ThisAndFuture',
  SelectedDates: 'SelectedDates',
} as const
export type TaskCompletionScope = Enum<typeof TaskCompletionScopes>

export type SetTaskCompletionRequest = {
  eventId: number
  completed: boolean
  scope: TaskCompletionScope
  start?: string | null
  end?: string | null
}

export const setTaskCompletion = async ({ eventId, ...body }: SetTaskCompletionRequest): Promise<void> => {
  return (await api.put(`/web/scheduling/tasks/${eventId}/completion`, body)).data
}

export type BulkUpdateScheduledEventTimeRequest = {
  eventUpdates: {
    eventId: number
    start: string
    end: string
  }[]
}

export const bulkUpdateScheduledEventTime = async (request: BulkUpdateScheduledEventTimeRequest): Promise<void> => {
  return (await api.post(`/web/scheduling/events/bulk-time-update`, request)).data
}

export type UpdateScheduledEventRequest = {
  eventId: number
  start: string
  end: string
  dayStartTime: string | null
  estimatedDurationInMinutes: number
  crewMemberIds: string
  crewLeadId: number | null
  workareaIds: string
  routeId: number | null
  repeatingEventConfig?: RepeatingEventConfig | null
  allDay: boolean
  sendCrewLeadNotifications: boolean
  title?: string | null
  notes?: string | null
  frozen: boolean
}

export type UpdateScheduledEventsRangeRequest = {
  updateRequest: UpdateScheduledEventRequest
  start: string
  end: string
}

export const updateScheduledEvent = async (request: UpdateScheduledEventRequest): Promise<void> => {
  return (await api.put(`/web/scheduling/event/${request.eventId}`, request)).data
}

export const updateScheduledEventsRange = async (request: UpdateScheduledEventsRangeRequest): Promise<void> => {
  return (await api.put(`/web/scheduling/event/${request.updateRequest.eventId}/range`, request)).data
}

export const updateFutureScheduledEvents = async (request: UpdateScheduledEventRequest): Promise<void> => {
  return (await api.put(`/web/scheduling/event/${request.eventId}/future`, request)).data
}

export type VisitAttachment = {
  id: string
  name: string
  dateTime: string
  fileDateUtc: string
  size: string
  folder: string
  tags: string
  thumbnail: string | null
  jobId: number | null
  visitId: string
  icon: string
  path: string
  url: string
}

export const defaultVisitAttachment = (): VisitAttachment => ({
  id: '',
  name: '',
  dateTime: '',
  fileDateUtc: '',
  size: '',
  folder: '',
  tags: '',
  thumbnail: null,
  jobId: null,
  visitId: '',
  icon: '',
  path: '',
  url: '',
})

export const getVisitAttachments = async (visitId: string): Promise<VisitAttachment[]> => {
  return (await api.get(`/web/attachments/visits/${visitId}`)).data
}

export const deleteAttachment = async (attachmentId: string): Promise<void> => {
  return (await api.delete(`/web/attachments/${attachmentId}`)).data
}

export type ConsultationCalendarEvent = {
  id: number
  consultationId: number
  startUtc: string
  start: string
  endUtc: string
  end: string
  allDay: boolean
  consultationStatus: number
  organizerEmail: string
  organizerColor: string
  consultation: {
    jobId: number | null
    timeFrame: string | null
    bestTimeToCall: string | null
    fee: string | null
    salesLeads: string[]
    emailTemplateId: number | null
    summary: string | null
    description: string | null
    notes: string | null
    emailBody: string | null
  }
  customer: {
    id: number
    displayName: string
    address1: string | null
    address2: string | null
    city: string | null
    state: string | null
    zip: string | null
  }
}

export type ConsultationCalendarResponse = {
  consultations: ConsultationCalendarEvent[]
}

export type TaskCalendarEvent = {
  id: number
  startUtc: string
  start: string
  endUtc: string
  end: string
  allDay: boolean
  title: string
  notes: string | null
  crewForemanEmail: string | null
  crewForemanColor: string | null
  crewMemberIds: string | null
  sendCrewLeadNotifications: boolean
  repeatingId: string | null
  completedAt: string | null
  frozen: boolean
}

export type TaskCalendarResponse = {
  tasks: TaskCalendarEvent[]
}

export const loadTasksByDateRange = async (startDate: string, endDate: string): Promise<TaskCalendarResponse> => {
  return (await api.get(`/web/scheduling/tasks?startDate=${startDate}&endDate=${endDate}`)).data
}

export const loadConsultationsByDateRange = async (startDate: string, endDate: string): Promise<ConsultationCalendarResponse> => {
  return (await api.get(`/web/scheduling/consultations?startDate=${startDate}&endDate=${endDate}`)).data
}

export const updateConsultationTime = async (id: number, startUtc: string, endUtc: string): Promise<void> => {
  await api.put(`/web/scheduling/consultation/${id}`, { startUtc, endUtc })
}

export type SchedulingWorkArea = {
  id: number
  name: string
  recurringScheduleConfig: string | null
  estimatedOccurrences: number | null
  pricingType: PricingType
  isApproved: boolean
  isOnHold: boolean
  estimatedHours: number
  scheduledEventCount: number
  sortIndex: number
  groupSortIndex: number
}

export const getSchedulingWorkAreas = async (jobId: number): Promise<SchedulingWorkArea[]> => {
  const response = await api.get<{ workAreas: SchedulingWorkArea[] }>(`/web/scheduling/jobs/${jobId}/work-areas`)
  return response.data.workAreas
}

export const RescheduleReasons = {
  NoVisitRecorded: 1,
  WorkAreaIncomplete: 2,
} as const
export type RescheduleReason = Enum<typeof RescheduleReasons>

export type RescheduleCandidateWorkArea = {
  workareaId: number
  workareaName: string
  status: VisitWorkareaStatus
}

export type RescheduleCandidate = {
  calendarEventId: number
  scheduledStartUtc: string
  scheduledEndUtc: string
  allDay: boolean
  jobId: number
  jobName: string
  jobNumber?: string
  customerId: number
  customerDisplayName: string
  address1: string
  address2: string | null
  city: string
  state: string
  zip: string
  schedulingNotes: string | null
  type: ProjectType
  allDivisionIds: string
  crewForemanEmail: string | null
  crewMemberIds: string | null
  workAreaIds: string | null
  notes: string | null
  notifications: boolean
  reason: RescheduleReason
  incompleteWorkAreas: RescheduleCandidateWorkArea[]
  lastVisitDateUtc: string | null
}

export const getRescheduleCandidates = async (startDate: string, endDate: string): Promise<RescheduleCandidate[]> => {
  return (await api.get(`/web/scheduling/reschedule-candidates`, { params: { startDate, endDate } })).data
}

export type RescheduleEventNewEvent = {
  jobId: number
  start: string
  end: string
  dayStartTime: string | null
  allDay: boolean
  estimatedDurationInMinutes: number
  crewLeadId: number | null
  crewMemberIds: string | null
  workAreaIds: string | null
  routeId: number | null
  repeatingEventConfig: RepeatingEventConfig | null
  notifications: boolean
  notes: string | null
}

export type RescheduleEventRequest = {
  originalEventId: number
  newEvent: RescheduleEventNewEvent
}

export type RescheduleEventResponse = {
  newEventId: number
}

export const rescheduleEvent = async (request: RescheduleEventRequest): Promise<RescheduleEventResponse> => {
  return (await api.post(`/web/scheduling/reschedule-event`, request)).data
}
export type CalendarEventSearchResult = {
  id: number
  jobId: number | null
  jobName: string
  jobNumber: string | null
  customerDisplayName: string | null
  propertyNickname: string | null
  startUtc: string
  endUtc: string
  allDay: boolean
  crewForemanEmail: string | null
}

export type CalendarEventSearchResponse = {
  results: CalendarEventSearchResult[]
  hasNextPage: boolean
}

export const searchScheduledEvents = async (searchTerm: string, page: number, pageSize: number): Promise<CalendarEventSearchResponse> => {
  const params = new URLSearchParams({ q: searchTerm, page: String(page), pageSize: String(pageSize) })
  return (await api.get(`/web/scheduling/events/search?${params}`)).data
}

export type EventDetailResponse = {
  event: ScheduledEvent
  calendarEventVisits: CalendarEventVisit[]
  visits: Visit[]
  visitWorkareaStatuses: VisitWorkareaStatusRecord[]
  timesheets: Timesheet[]
}

export const loadEventDetail = async (eventId: number): Promise<EventDetailResponse> => {
  return (await api.get(`/web/scheduling/events/detail/${eventId}`)).data
}

export type PrintCrewDaySchedulesRequest = {
  date: string
  includeConsultations: boolean
  includeTasks: boolean
}

export type PrintCrewDaySchedulesResult = {
  archive: Blob | null
  fileName: string
  sheetCount: number
}

export const printCrewDaySchedules = async (request: PrintCrewDaySchedulesRequest): Promise<PrintCrewDaySchedulesResult> => {
  const response = await api.post(`/web/scheduling/day-schedules/print`, request, { responseType: 'blob' })

  const contentType = response.headers?.['content-type'] ?? ''
  if (!contentType.includes('application/zip')) {
    return { archive: null, fileName: '', sheetCount: 0 }
  }

  return {
    archive: response.data,
    fileName: `crew-schedules-${request.date}.zip`,
    sheetCount: 1
  }
}
